import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, Eye, FilePlus2, Loader2, PenLine, Users, X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import useModalA11y from "@/lib/useModalA11y";

const dl = async (url, name) => {
  const r = await api.get(url, { responseType: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(r.data); a.download = name; a.click(); URL.revokeObjectURL(a.href);
};

export function AnkleResponseView({ responseId, onClose }) {
  const [rec, setRec] = useState(null);
  const [note, setNote] = useState("");
  const ref = useModalA11y({ open: true, onClose });
  const load = () => api.get(`/ankle-finder/responses/${responseId}`).then((r) => setRec(r.data)).catch(() => toast.error("Could not load response"));
  const q4Labels = Object.fromEntries(((rec?.questions || []).find((q) => q.id === "q4_character")?.options || []).map((o) => [o.id, o.label]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [responseId]);
  const amend = async () => {
    if (!note.trim()) return;
    try { await api.post(`/ankle-finder/responses/${responseId}/amend`, { note }); setNote(""); load(); toast.success("Amendment recorded"); }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-start md:items-center justify-center p-3 overflow-auto" onClick={onClose}>
      <div ref={ref} role="dialog" aria-modal="true" className="fl-card fl-card-elevated w-full max-w-3xl my-4 max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="ankle-response-modal">
        <div className="p-4 md:p-6 border-b flex items-start justify-between sticky top-0 z-10" style={{ borderColor: "var(--fl-border)", background: "var(--fl-card)" }}>
          <div>
            <div className="fl-label text-[#f47a20]">Ankle Programme Finder response</div>
            {rec && <div className="text-xs text-zinc-500 mt-1">{rec.version} · {new Date(rec.created_at).toLocaleString("en-GB")} · entered by {rec.entered_by?.role === "patient" ? "patient" : `clinician (${rec.entered_by?.name || ""})`}</div>}
          </div>
          <div className="flex gap-2">
            <button className="fl-btn-ghost text-xs" onClick={() => dl(`/ankle-finder/responses/${responseId}/pdf`, `ankle-finder-${responseId.slice(0, 8)}.pdf`)} data-testid="ankle-response-pdf"><Download size={12} /> PDF</button>
            <button onClick={onClose} aria-label="Close" data-testid="ankle-response-close"><X size={16} className="text-zinc-400" /></button>
          </div>
        </div>
        {!rec ? <div className="p-8 text-center text-sm text-zinc-500"><Loader2 className="animate-spin inline mr-2" size={14} /> Loading…</div> : (
          <div className="p-4 md:p-6 space-y-4 text-sm">
            <div className="fl-card p-4 border border-[#f47a20]/40" data-testid="ankle-response-outcome">
              <div className="fl-label text-[#f47a20]">Outcome</div>
              <div className="fl-heading text-lg mt-1">{rec.outcome}{rec.pathway_label ? ` — ${rec.pathway_label}` : ""}</div>
              <div className="text-xs text-zinc-400 mt-1">Programme availability: {rec.availability} · Rules: {rec.rules_status}</div>
              {rec.flags?.length > 0 && <div className="text-xs text-amber-300 mt-1">Flags: {rec.flags.join(", ")}</div>}
            </div>
            <div className="space-y-3">
              {rec.questions.filter((q) => rec.answers[q.id] !== undefined || rec.comments?.[q.id]).map((q) => {
                const g = rec.answers[q.id]; const vals = Array.isArray(g) ? g : [g];
                return (
                  <div key={q.id} className="border-b border-white/5 pb-2" data-testid={`ankle-answer-${q.id}`}>
                    <div className="text-xs text-zinc-500">{q.stage}</div>
                    <div className="text-zinc-200">{q.label}</div>
                    <div className="text-[#f47a20] mt-0.5">{q.options.filter((o) => vals.includes(o.id)).map((o) => o.label).join("; ") || "—"}</div>
                    {rec.comments?.[q.id] && <div className="text-xs text-zinc-400 italic mt-0.5">Patient's words: "{rec.comments[q.id]}"</div>}
                    {q.ui === "pain_map" && rec.pain_markers?.length > 0 && (
                      <div className="mt-2 space-y-2" data-testid="ankle-response-markers">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {["front", "back", "outside", "inside"].filter((v) => rec.pain_markers.some((m) => m.view === v)).map((v) => (
                            <div key={v} className="relative rounded border border-white/10 overflow-hidden bg-[#F3F4F6]">
                              <img src={`/ankle/view-${v}.png`} alt={`${v} view`} className="w-full h-auto block" />
                              {rec.pain_markers.map((m, i) => m.view === v && (
                                <span key={m.id} className="absolute w-5 h-5 -ml-2.5 -mt-2.5 rounded-full bg-[#f47a20] text-black text-[10px] font-bold flex items-center justify-center border border-black" style={{ left: `${m.x_pct * 100}%`, top: `${m.y_pct * 100}%` }}>{i + 1}</span>
                              ))}
                              <div className="absolute bottom-0 left-0 text-[9px] px-1 bg-black/60 text-white capitalize">{v}</div>
                            </div>
                          ))}
                        </div>
                        {rec.pain_markers.map((m, i) => (
                          <div key={m.id} className="text-xs text-zinc-300" data-testid={`ankle-response-marker-${i + 1}`}>
                            <b className="text-[#f47a20]">#{i + 1}</b>{rec.main_marker === m.id ? " (main)" : ""} · <span className="capitalize">{m.view}</span> · {m.foot} foot · {(m.types || []).map((t) => q4Labels[t] || t).join("; ") || "no type"} · intensity {m.intensity == null ? "not answered" : m.intensity === 0 ? "0 (no pain now)" : m.intensity}{m.comment ? ` · "${m.comment}"` : ""}
                          </div>
                        ))}
                        {rec.main_marker === "none" && <div className="text-xs text-zinc-400">Main concern: no single main area.</div>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div>
              <div className="fl-label text-[#f47a20]">Clinician amendments</div>
              {(rec.amendments || []).map((a) => <div key={a.id} className="text-xs text-zinc-300 mt-1">{new Date(a.at).toLocaleString("en-GB")} · <b>{a.by_name}</b>: {a.note}</div>)}
              <div className="flex gap-2 mt-2">
                <input className="fl-input text-sm flex-1" placeholder="Add an attributed amendment (original answers are preserved)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="ankle-amend-input" />
                <button className="fl-btn text-xs" onClick={amend} data-testid="ankle-amend-save"><PenLine size={12} /> Add</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DraftEditor({ version, onClose, onSaved }) {
  const [qs, setQs] = useState(version.questions);
  const [errors, setErrors] = useState([]);
  const [approver, setApprover] = useState("");
  const { user } = useAuth();
  const ref = useModalA11y({ open: true, onClose });
  const upd = (qi, patch) => setQs((a) => a.map((q, i) => (i === qi ? { ...q, ...patch } : q)));
  const updOpt = (qi, oi, patch) => upd(qi, { options: qs[qi].options.map((o, j) => (j === oi ? { ...o, ...patch } : o)) });
  const save = async () => {
    try { await api.patch(`/ankle-finder/versions/${version.id}`, { questions: qs }); setErrors([]); toast.success("Draft saved"); onSaved(); }
    catch (e) { const d = e?.response?.data?.detail; setErrors(d?.errors || [typeof d === "string" ? d : "Save failed"]); }
  };
  const publish = async () => {
    try { await api.post(`/ankle-finder/versions/${version.id}/publish`, { clinically_approved: true, approved_by: approver, notes: "Published from Forms editor" }); toast.success("Published — future submissions use the new version"); onSaved(); onClose(); }
    catch (e) { const d = e?.response?.data?.detail; setErrors(d?.errors || [typeof d === "string" ? d : "Publish failed"]); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-start justify-center p-3 overflow-auto" onClick={onClose}>
      <div ref={ref} role="dialog" aria-modal="true" className="fl-card fl-card-elevated w-full max-w-3xl my-4 max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="ankle-draft-editor">
        <div className="p-4 border-b flex justify-between sticky top-0 z-10" style={{ borderColor: "var(--fl-border)", background: "var(--fl-card)" }}>
          <div><div className="fl-label text-[#f47a20]">Draft · {version.version}</div><div className="text-xs text-zinc-500">Edit wording, help text and option labels. Conditional rules and answer types require clinical approval and are read-only.</div></div>
          <button onClick={onClose} aria-label="Close"><X size={16} className="text-zinc-400" /></button>
        </div>
        <div className="p-4 space-y-4">
          {errors.length > 0 && <div className="fl-card p-3 text-xs text-red-300 border border-red-500/40" data-testid="ankle-draft-errors">{errors.map((e, i) => <div key={i}>{e}</div>)}</div>}
          {qs.map((q, qi) => (
            <div key={q.id} className="border border-white/10 rounded-md p-3" data-testid={`draft-q-${q.id}`}>
              <div className="text-[10px] text-zinc-500">{q.id} · {q.kind} · {q.stage}{q.show_if ? " · conditional (rule locked)" : ""}</div>
              <input className="fl-input text-sm w-full mt-1" value={q.label} onChange={(e) => upd(qi, { label: e.target.value })} data-testid={`draft-label-${q.id}`} />
              <input className="fl-input text-xs w-full mt-1" placeholder="Help text" value={q.help || ""} onChange={(e) => upd(qi, { help: e.target.value })} />
              <div className="mt-2 space-y-1">
                {q.options.map((o, oi) => (
                  <div key={o.id} className="flex gap-2 items-center">
                    <span className="text-[10px] text-zinc-600 w-28 truncate">{o.id}</span>
                    <input className="fl-input text-xs flex-1" value={o.label} onChange={(e) => updOpt(qi, oi, { label: e.target.value })} />
                    <button type="button" className="text-red-400" aria-label="Remove option" onClick={() => upd(qi, { options: q.options.filter((_, j) => j !== oi) })}><X size={12} /></button>
                  </div>
                ))}
                <button type="button" className="text-xs text-[#f47a20]" onClick={() => upd(qi, { options: [...q.options, { id: `opt_${Date.now().toString(36)}`, label: "New option" }] })}>+ Add option</button>
              </div>
            </div>
          ))}
        </div>
        <div className="p-4 border-t flex flex-wrap gap-2 items-center sticky bottom-0" style={{ borderColor: "var(--fl-border)", background: "var(--fl-card)" }}>
          <button className="fl-btn text-xs" onClick={save} data-testid="ankle-draft-save">Save draft</button>
          {user?.role === "admin" && (
            <>
              <input className="fl-input text-xs" placeholder="Clinical approver name" value={approver} onChange={(e) => setApprover(e.target.value)} data-testid="ankle-draft-approver" />
              <button className="fl-btn-ghost text-xs" disabled={!approver.trim()} onClick={publish} data-testid="ankle-draft-publish">Publish (approved)</button>
            </>
          )}
          <span className="text-[10px] text-zinc-500 ml-auto">Previous responses keep their original wording and result.</span>
        </div>
      </div>
    </div>
  );
}

export function AnkleFinderPanel({ patients }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [responses, setResponses] = useState([]);
  const [versions, setVersions] = useState([]);
  const [pathways, setPathways] = useState([]);
  const [waitlist, setWaitlist] = useState(null);
  const [settings, setSettings] = useState({ launch_target_date: "", offer_text: "" });
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [walkIn, setWalkIn] = useState("");
  const isAdmin = user?.role === "admin";

  const load = async () => {
    const [r, v, p, w, d] = await Promise.all([
      api.get("/ankle-finder/responses"), api.get("/ankle-finder/versions"), api.get("/admin/ankle-finder/pathways"),
      api.get("/ankle-finder/waitlist/summary"), api.get("/ankle-finder/definition"),
    ]);
    setResponses(r.data); setVersions(v.data); setPathways(p.data); setWaitlist(w.data);
    setSettings({ launch_target_date: d.data.settings?.launch_target_date || "", offer_text: d.data.settings?.offer_text || "" });
  };
  useEffect(() => { if (open) load().catch(() => toast.error("Could not load Ankle Finder data")); }, [open]);

  const createDraft = async () => { const r = await api.post("/ankle-finder/versions/draft"); setEditing(r.data); load(); };
  const savePathway = async (pw, patch) => {
    try { await api.put(`/admin/ankle-finder/pathways/${pw.pathway}`, patch); toast.success("Saved"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const saveSettings = async () => {
    try { await api.put("/admin/ankle-finder/settings", { launch_target_date: settings.launch_target_date || null, offer_text: settings.offer_text || null }); toast.success("Settings saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="fl-card fl-card-elevated p-5 md:col-span-2 border-[#f47a20]/40" data-testid="ankle-finder-panel">
      <div className="flex justify-between items-start gap-3 flex-wrap">
        <div>
          <div className="fl-label text-[#f47a20]">Screening questionnaire · branching</div>
          <div className="fl-heading text-lg mt-1">Ankle Programme Finder</div>
          <div className="text-xs text-zinc-400 mt-1">Start the Ankle Finder questionnaire for a patient, assign via the form workflow, download blank/completed PDFs, edit a draft version.</div>
        </div>
        <button className="fl-btn-ghost text-xs" onClick={() => setOpen((o) => !o)} data-testid="ankle-panel-toggle">{open ? "Hide" : "Open panel"}</button>
      </div>
      <div className="mt-4 flex gap-2 flex-wrap items-center">
        <select className="fl-input text-xs" value={walkIn} onChange={(e) => setWalkIn(e.target.value)} data-testid="ankle-walkin-patient">
          <option value="">Ankle Finder: choose patient…</option>
          {patients.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.email}</option>)}
        </select>
        <button className="fl-btn text-xs" disabled={!walkIn} onClick={() => navigate(`/online-programmes/ankle/finder?patient=${walkIn}`)} data-testid="ankle-walkin-start"><FilePlus2 size={12} /> Start Ankle Finder</button>
        <button className="fl-btn-ghost text-xs" onClick={() => dl("/ankle-finder/blank.pdf", "ankle-programme-finder-blank.pdf")} data-testid="ankle-blank-pdf"><Download size={12} /> Blank PDF</button>
        <button className="fl-btn-ghost text-xs" onClick={createDraft} data-testid="ankle-new-draft"><PenLine size={12} /> Edit draft version</button>
      </div>

      {open && (
        <div className="mt-6 grid lg:grid-cols-2 gap-6">
          <div>
            <div className="fl-label text-[#f47a20] mb-2">Completed responses ({responses.length})</div>
            <div className="divide-y divide-white/5 max-h-80 overflow-auto" data-testid="ankle-response-list">
              {responses.length === 0 && <div className="text-xs text-zinc-500 py-3">No responses yet.</div>}
              {responses.map((r) => (
                <div key={r.id} className="py-2 flex items-center gap-3 text-xs" data-testid={`ankle-response-row-${r.id}`}>
                  <div className="flex-1 min-w-0"><div className="text-zinc-200 truncate">{r.patient_name} <span className="text-zinc-500">· {r.patient_email}</span></div>
                    <div className="text-zinc-500">{new Date(r.created_at).toLocaleString("en-GB")} · {r.outcome}{r.pathway_label ? ` · ${r.pathway_label}` : ""} · by {r.entered_by?.role === "patient" ? "patient" : "clinician"}</div></div>
                  <button className="fl-btn-ghost text-xs" onClick={() => setViewing(r.id)} data-testid={`ankle-response-view-${r.id}`}><Eye size={12} /> View</button>
                </div>
              ))}
            </div>
            <div className="fl-label text-[#f47a20] mt-6 mb-2">Versions</div>
            {versions.map((v) => (
              <div key={v.id} className="text-xs flex items-center gap-2 py-1" data-testid={`ankle-version-${v.id}`}>
                <span className={`px-1.5 py-0.5 rounded-full text-[9px] uppercase ${v.status === "published" ? "bg-emerald-500/10 text-emerald-400" : v.status === "draft" ? "bg-amber-500/10 text-amber-400" : "bg-white/5 text-zinc-500"}`}>{v.status}</span>
                <span className="text-zinc-300">{v.version}</span>
                {v.status === "draft" && <button className="text-[#f47a20] hover:underline" onClick={() => setEditing(v)} data-testid={`ankle-version-edit-${v.id}`}>Edit</button>}
                <button className="text-zinc-400 hover:underline" onClick={() => dl(`/ankle-finder/blank.pdf?version_id=${v.id}`, `ankle-finder-${v.version}.pdf`)}>Blank PDF</button>
              </div>
            ))}
          </div>
          <div>
            <div className="fl-label text-[#f47a20] mb-2 flex items-center gap-1"><Users size={12} /> Waiting list · {waitlist?.total ?? 0} signups</div>
            <div className="text-xs space-y-1" data-testid="ankle-waitlist-summary">
              {(waitlist?.by_interest || []).map((w) => <div key={w.pathway} className="flex justify-between"><span className="text-zinc-300">{w.label}</span><span className="text-[#f47a20]">{w.count}</span></div>)}
              {!waitlist?.by_interest?.length && <div className="text-zinc-500">No signups yet.</div>}
            </div>
            <div className="fl-label text-[#f47a20] mt-6 mb-2">Pathway → programme</div>
            <div className="text-[11px] text-zinc-500 mb-2">A pathway becomes purchasable only when an approved, active online programme is attached and "approved for sale" is on. PDF and interactive My Plan are different delivery types.</div>
            <div className="space-y-2 max-h-80 overflow-auto" data-testid="ankle-pathway-config">
              {pathways.map((pw) => (
                <div key={pw.pathway} className="text-xs border border-white/10 rounded-md p-2" data-testid={`ankle-pathway-${pw.pathway}`}>
                  <div className="flex justify-between"><span className="text-zinc-200">{pw.label}</span><span className={pw.approved_for_sale ? "text-emerald-400" : "text-zinc-500"}>{pw.approved_for_sale ? "For sale" : pw.purchasable_intent ? "Unavailable" : "Waiting list / assessment"}</span></div>
                  {isAdmin && pw.purchasable_intent && (
                    <div className="flex gap-1 mt-1 flex-wrap">
                      <input className="fl-input text-[11px] flex-1" placeholder="package_id (online programme)" defaultValue={pw.package_id || ""} onBlur={(e) => e.target.value !== (pw.package_id || "") && savePathway(pw, { package_id: e.target.value })} />
                      <select className="fl-input text-[11px]" value={pw.delivery_type || "pdf"} onChange={(e) => savePathway(pw, { delivery_type: e.target.value })}><option value="pdf">PDF</option><option value="interactive_plan">Interactive My Plan</option></select>
                      <button className={`text-[11px] px-2 rounded ${pw.approved_for_sale ? "bg-red-500/10 text-red-300" : "bg-emerald-500/10 text-emerald-300"}`} onClick={() => savePathway(pw, { approved_for_sale: !pw.approved_for_sale })} data-testid={`ankle-pathway-approve-${pw.pathway}`}>{pw.approved_for_sale ? "Withdraw" : "Approve for sale"}</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {isAdmin && (
              <div className="mt-6">
                <div className="fl-label text-[#f47a20] mb-2">Launch settings (hidden until set)</div>
                <div className="flex gap-2 flex-wrap">
                  <input type="date" className="fl-input text-xs" value={settings.launch_target_date} onChange={(e) => setSettings({ ...settings, launch_target_date: e.target.value })} data-testid="ankle-settings-date" />
                  <input className="fl-input text-xs flex-1" placeholder="Offer text (optional)" value={settings.offer_text} onChange={(e) => setSettings({ ...settings, offer_text: e.target.value })} data-testid="ankle-settings-offer" />
                  <button className="fl-btn text-xs" onClick={saveSettings} data-testid="ankle-settings-save">Save</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {viewing && <AnkleResponseView responseId={viewing} onClose={() => setViewing(null)} />}
      {editing && <DraftEditor version={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}
